import React, { useEffect, useState } from "react";
import { Card, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import {
  telephoneNumberList,
  thirdPartyVapiList,
} from "../../../../store/thirdPartyIntegration/action";
import TelephonyOnboardingWizard from "../../../../Components/popUpComponents/TelephonyOnboarding/TelephonyOnboardingWizard";

// Strip everything but digits so numbers match regardless of +, spaces, etc.
const normaliseNumber = (n) => (n || "").toString().replace(/\D/g, "");

// Phone Numbers: the dedicated screen half of the "both launch points" for the
// telephony onboarding. Lists the company's Twilio numbers, shows which virtual
// receptionist answers each, and opens the TelephonyOnboardingWizard to order
// more (entryMode="number") or set up a receptionist (entryMode="receptionist").
export default function PhoneNumbers() {
  const dispatch = useDispatch();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState("number");

  const openWizard = (mode) => {
    setWizardMode(mode);
    setWizardOpen(true);
  };

  const { listDataAPI, listLoading, receptionistListDataAPI } = useSelector(
    (state) => ({
      listDataAPI: state.ThirdPartyIntegration.telephoneNumberListData,
      listLoading: state.ThirdPartyIntegration.telephoneNumberListLoading,
      receptionistListDataAPI:
        state.ThirdPartyIntegration.thirdPartyVapiListData,
    })
  );

  useEffect(() => {
    dispatch(telephoneNumberList());
    dispatch(thirdPartyVapiList());
  }, []);

  const numbers = listDataAPI?.telephonenumberaccount_list || [];
  const receptionists = receptionistListDataAPI?.receptionist_list || [];

  // Find the receptionist whose linked twilio_number matches this number.
  const receptionistFor = (telephoneNumber) => {
    const key = normaliseNumber(telephoneNumber);
    if (!key) return null;
    return (
      receptionists.find((r) => normaliseNumber(r.twilio_number) === key) || null
    );
  };

  return (
    <Card className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-1">Phone Numbers</h5>
          <p className="text-muted mb-0">
            Twilio numbers owned by your company. Order a number to also add an
            AI Virtual Receptionist.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => openWizard("number")}>
          <i className="ri-add-line me-1"></i> Order a number
        </button>
      </div>

      {listLoading ? (
        <LoadingSpinner />
      ) : numbers.length > 0 ? (
        <Table responsive className="align-middle mb-0">
          <thead>
            <tr>
              <th>Number</th>
              <th>Nickname</th>
              <th>Country</th>
              <th>Voice</th>
              <th>Receptionist</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {numbers.map((n) => {
              const active = !n.CancelledDateTime;
              const receptionist = receptionistFor(n.TelephoneNumber);
              return (
                <tr key={n.Id}>
                  <td className="fw-bold">{n.TelephoneNumber}</td>
                  <td>{n.Nickname || "-"}</td>
                  <td>{n.CountryCode || "-"}</td>
                  <td>
                    {String(n.FlagVoiceEnabled) === "1" ? (
                      <span className="badge bg-success-subtle text-success">
                        Enabled
                      </span>
                    ) : (
                      <span className="badge bg-secondary-subtle text-secondary">
                        Off
                      </span>
                    )}
                  </td>
                  <td>
                    {receptionist ? (
                      <span className="badge bg-primary-subtle text-primary">
                        <i className="ri-robot-2-line me-1"></i>
                        {receptionist.name || "Receptionist"}
                      </span>
                    ) : (
                      <button
                        className="btn btn-sm btn-link p-0 text-decoration-none"
                        onClick={() => openWizard("receptionist")}
                      >
                        No receptionist — Set up
                      </button>
                    )}
                  </td>
                  <td>
                    {active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-danger">Cancelled</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <div className="text-center text-muted py-5">
          <i className="ri-phone-line fs-1 d-block mb-2 opacity-50"></i>
          <p className="mb-3">You don&apos;t have any phone numbers yet.</p>
          <button className="btn btn-primary" onClick={() => openWizard("number")}>
            <i className="ri-add-line me-1"></i> Order your first number
          </button>
        </div>
      )}

      <TelephonyOnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        entryMode={wizardMode}
        onComplete={() => {
          dispatch(telephoneNumberList());
          dispatch(thirdPartyVapiList());
        }}
      />
    </Card>
  );
}
